// mds-dojo — dojo/date: add / difference / compare, ported from dojo/date.js
// (BSD-3, © JS Foundation — see LICENSE). Behavior-faithful for the intervals
// the app uses (day/week/month/year/hour/minute/second/millisecond/quarter/weekday).

export function add(date: Date, interval: string, amount: number): Date {
  const sum = new Date(+date);
  let fixOvershoot = false;
  let property: 'Date' | 'Month' | 'FullYear' | 'Hours' | 'Minutes' | 'Seconds' | 'Milliseconds' = 'Date';

  switch (interval) {
    case 'day':
      break;
    case 'weekday': {
      // Divide the increment time span into weekspans plus leftover days.
      const days = amount % 5;
      let weeks: number;
      if (!days) {
        weeks = amount ? amount / 5 : 0;
      } else {
        let adj = days;
        weeks = parseInt(String(amount / 5), 10);
        // Orient the leftover days around the current day of week.
        const strt = date.getDay();
        if (strt === 6 && amount > 0) { adj++; }
        else if (strt === 0 && amount < 0) { adj--; }
        const trgt = strt + adj;
        if (trgt === 0 || trgt === 6) { adj += (amount > 0 ? 2 : -2); }
        amount = (7 * weeks) + adj;
        sum.setDate(sum.getDate() + amount);
        // If the possibility exists that the day landed on a weekend, shift it.
        const day = sum.getDay();
        if (day === 6) { sum.setDate(sum.getDate() + (amount > 0 ? 2 : -1)); }
        else if (day === 0) { sum.setDate(sum.getDate() + (amount > 0 ? 1 : -2)); }
        return sum;
      }
      amount = (7 * weeks) + days;
      break;
    }
    case 'year':
      property = 'FullYear';
      fixOvershoot = true;
      break;
    case 'week':
      amount *= 7;
      break;
    case 'quarter':
      amount *= 3;
      // fall through
    case 'month':
      property = 'Month';
      fixOvershoot = true;
      break;
    case 'hour':
      property = 'Hours'; break;
    case 'minute':
      property = 'Minutes'; break;
    case 'second':
      property = 'Seconds'; break;
    case 'millisecond':
      property = 'Milliseconds'; break;
    default:
      break;
  }

  if (property) { (sum as any)['set' + property]((sum as any)['get' + property]() + amount); }
  if (fixOvershoot && (sum.getDate() < date.getDate())) { sum.setDate(0); }
  return sum;
}

export function difference(date1: Date, date2?: Date, interval?: string): number {
  date2 = date2 || new Date();
  interval = interval || 'day';
  const yearDiff = date2.getFullYear() - date1.getFullYear();
  let delta = 1;

  switch (interval) {
    case 'quarter': {
      const m1 = date1.getMonth();
      const m2 = date2.getMonth();
      const q1 = Math.floor(m1 / 3) + 1;
      const q2 = Math.floor(m2 / 3) + 1 + (yearDiff * 4);
      delta = q2 - q1;
      break;
    }
    case 'weekday': {
      // exact port of dojo/date.js "weekday"
      let days = Math.round(difference(date1, date2, 'day'));
      let weeks = parseInt(String(difference(date1, date2, 'week')), 10);
      let mod = days % 7;
      if (mod === 0) {
        days = weeks * 5;
      } else {
        let adj = 0;
        const aDay = date1.getDay();
        const bDay = date2.getDay();
        weeks = parseInt(String(days / 7), 10);
        mod = days % 7;
        const dtMark = new Date(+date1);
        dtMark.setDate(dtMark.getDate() + (weeks * 7));
        const dayMark = dtMark.getDay();
        if (days > 0) {
          switch (true) {
            case aDay === 6: adj = -1; break;
            case aDay === 0: adj = 0; break;
            case bDay === 6: adj = -1; break;
            case bDay === 0: adj = -2; break;
            case (dayMark + mod) > 5: adj = -2;
          }
        } else if (days < 0) {
          switch (true) {
            case aDay === 6: adj = 0; break;
            case aDay === 0: adj = 1; break;
            case bDay === 6: adj = 2; break;
            case bDay === 0: adj = 1; break;
            case (dayMark + mod) < 0: adj = 2;
          }
        }
        days += adj;
        days -= (weeks * 2);
      }
      delta = days;
      break;
    }
    case 'year':
      delta = yearDiff;
      break;
    case 'month':
      delta = (date2.getMonth() - date1.getMonth()) + (yearDiff * 12);
      break;
    case 'week':
      delta = parseInt(String(difference(date1, date2, 'day') / 7), 10);
      break;
    case 'day':
      delta = (+date2 - +date1) / (24 * 60 * 60 * 1000);
      break;
    case 'hour':
      delta = (+date2 - +date1) / (60 * 60 * 1000);
      break;
    case 'minute':
      delta = (+date2 - +date1) / (60 * 1000);
      break;
    case 'second':
      delta = (+date2 - +date1) / 1000;
      break;
    case 'millisecond':
      delta = +date2 - +date1;
      break;
    default:
      break;
  }
  return Math.round(delta);
}

export function getDaysInMonth(dateObject: Date): number {
  const month = dateObject.getMonth();
  const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 1 && isLeapYear(dateObject)) { return 29; }
  return days[month];
}

export function isLeapYear(dateObject: Date): boolean {
  const year = dateObject.getFullYear();
  return !(year % 400) || (!(year % 4) && !!(year % 100));
}

export function compare(date1: Date, date2?: Date, portion?: string): number {
  const d1 = new Date(+date1);
  const d2 = new Date(+(date2 || new Date()));
  if (portion === 'date') {
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
  } else if (portion === 'time') {
    d1.setFullYear(0, 0, 1);
    d2.setFullYear(0, 0, 1);
  }
  if (+d1 > +d2) { return 1; }
  if (+d1 < +d2) { return -1; }
  return 0;
}

const date = { add, difference, compare, getDaysInMonth, isLeapYear };
export default date;
